import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

/**
 * PropertyGallery
 * 4-column hero grid + full-screen lightbox with arrows and thumbnail strip.
 *
 * @param {string[]} images - non-empty array of image URLs
 * @param {string}  alt     - accessible label (property title)
 */
const PropertyGallery = ({ images = [], alt = 'Property image' }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [showGallery, setShowGallery] = useState(false);

  if (!images.length) return null;

  const safeIdx = Math.min(selectedImage, images.length - 1);
  const mainImage = images[safeIdx];
  const gridImages = images.slice(1, 5);

  const nextImage = () => setSelectedImage((p) => (p + 1) % images.length);
  const prevImage = () => setSelectedImage((p) => (p - 1 + images.length) % images.length);

  return (
    <>
      {/* Full Screen Gallery Modal */}
      {showGallery && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" data-testid="property-gallery-modal">
          <button
            onClick={() => setShowGallery(false)}
            className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
            aria-label="Close gallery"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          <button
            onClick={prevImage}
            className="absolute left-4 p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-8 h-8 text-white" />
          </button>

          <div className="max-w-5xl max-h-[80vh] px-16">
            <img
              src={images[safeIdx]}
              alt={`${alt} ${safeIdx + 1}`}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <p className="text-center text-white mt-4 text-lg">
              {safeIdx + 1} / {images.length}
            </p>
          </div>

          <button
            onClick={nextImage}
            className="absolute right-4 p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
            aria-label="Next image"
          >
            <ChevronRight className="w-8 h-8 text-white" />
          </button>

          {/* Thumbnail strip */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[90vw] pb-2">
            {images.map((img, idx) => (
              <img
                key={`${img}-${idx}`}
                src={img}
                alt={`Thumbnail ${idx + 1}`}
                onClick={() => setSelectedImage(idx)}
                className={`w-16 h-16 object-cover rounded cursor-pointer transition-all ${
                  safeIdx === idx ? 'ring-2 ring-white opacity-100' : 'opacity-60 hover:opacity-80'
                }`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Image Grid */}
      <div className="grid grid-cols-4 gap-3 rounded-3xl overflow-hidden relative" data-testid="property-gallery-grid">
        <div
          className="col-span-4 md:col-span-2 md:row-span-2 cursor-pointer"
          onClick={() => setShowGallery(true)}
        >
          <img
            src={mainImage}
            alt={alt}
            className="w-full h-full object-cover hover:opacity-95 transition-opacity"
            style={{ minHeight: '400px', maxHeight: '500px' }}
          />
        </div>
        {gridImages.map((image, index) => (
          <div
            key={`${image}-${index}`}
            className="col-span-2 md:col-span-1 aspect-square cursor-pointer"
            onClick={() => {
              setSelectedImage(index + 1);
              setShowGallery(true);
            }}
          >
            <img
              src={image}
              alt={`View ${index + 2}`}
              className="w-full h-full object-cover hover:opacity-90 transition-opacity"
            />
          </div>
        ))}

        {/* Show all photos button */}
        {images.length > 5 && (
          <button
            onClick={() => setShowGallery(true)}
            className="absolute bottom-6 right-6 bg-background text-foreground px-5 py-2.5 rounded-full shadow-lg font-medium text-sm hover:bg-muted transition-colors inline-flex items-center gap-2"
            data-testid="gallery-view-all-btn"
          >
            <span>View all {images.length} photos</span>
          </button>
        )}
      </div>
    </>
  );
};

export default PropertyGallery;
